import FieldDestinationProvider from "../common/fields/FieldDestinationProvider.tsx";
import { Box, Divider, Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import type { Report } from "../../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../../api/types/svTypes.ts";
import GlobalDndProvider from "../../../../../../common/GlobalDndProvider.tsx";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch";
import { selectIsNewReport, selectResetId } from "../../../store/selectors/currentReportSelectors";
import { selectEditDisabled, selectVersionHistoryOpen } from "../../../store/selectors/uiSettingsSelectors.ts";
import { createCurrentReportAction } from "../../../store/utils/storeUtils";
import CollapseContainer from "../../common/CollapseContainer.tsx";
import EntityLevelAccess from "../../common/EntityLevelAccess.tsx";
import ReportProductName from "../../common/ReportProductName.tsx";
import ReportSelector from "../../common/report-selector/ReportSelector.tsx";
import PartContainer from "../../viewer/common/PartContainer.tsx";
import ReportActionHeader from "../ReportActionHeader.tsx";
import { ExportStateContextProvider } from "../common/contexts/ExportStateContextProvider.tsx";
import CustomDragLayer from "../common/dragLayer/CustomDragLayer.tsx";
import FieldsComponent from "../common/fields/Fields.tsx";
import ExportAction from "../common/saving/ExportAction.tsx";
import MoreAction from "../common/saving/MoreAction.tsx";
import SaveButton from "../common/saving/SaveButton.tsx";
import ShareReportButton from "../common/sharing/ShareReportButton.tsx";
import { DeveloperPanelWrapper } from "../dev/DeveloperPanelWrapper.tsx";
import TabularContainer from "../tabular/preview/TabularContainer.tsx";
import VersionHistoryPanel from "../version-history/VersionHistoryPanel.tsx";
import useVersionHistoryUrlParam from "../version-history/useVersionHistoryUrlParam.ts";
import ReportEditor from "./editor/ApiTabularEditor.tsx";

interface Props {
  report: Report;
}

const EditReportPage = ({ report }: Props) => {
  const dispatch = useAppDispatch();
  const isNewReport = useSelector(selectIsNewReport);
  const { hasAnyPermission } = useClientPermissionsContext();
  const resetId = useSelector(selectResetId);
  const editDisabled = useSelector(selectEditDisabled);
  const versionHistoryOpen = useSelector(selectVersionHistoryOpen);

  useVersionHistoryUrlParam();

  useEffect(() => {
    dispatch(createCurrentReportAction("updateReportMode", true));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  if (report === undefined) return <></>;

  if (!hasAnyPermission(["ManageReports"])) {
    return <Navigate to={".."} />;
  }

  return (
    <ExportStateContextProvider disabled={editDisabled}>
      <GlobalDndProvider>
        <FieldDestinationProvider key={`${report.reportId}-${resetId}`}>
          <Stack sx={{ height: "100%" }}>
            <ReportActionHeader
              ProductName={<ReportProductName title="API Reporting" />}
              ReportSelector={
                <ReportSelector
                  requiredAccess={ReportAccessType.Write}
                  groups={undefined}
                  readonlyMode={editDisabled}
                />
              }
              readonlyMode={editDisabled}
            >
              <Stack direction={"row"} sx={{ alignItems: "center", gap: 1 }}>
                <EntityLevelAccess />
                <ShareReportButton disabled={editDisabled} />
                <ExportAction />
                <SaveButton report={report} disabled={editDisabled} />
                {!isNewReport && <MoreAction report={report} readonlyMode={editDisabled} />}
              </Stack>
            </ReportActionHeader>
            <Divider />
            <Box sx={{ display: "flex", overflowY: "auto", height: "100%" }}>
              <CollapseContainer
                expandCollapsePaddingTop={25}
                collapseElement={
                  <Typography
                    variant="h6"
                    sx={(theme) => ({ color: theme.palette.text.primary, transform: "rotate(90deg)" })}
                  >
                    Fields
                  </Typography>
                }
              >
                <PartContainer sx={{ maxWidth: "21rem", minWidth: "21rem" }} caption={<Box></Box>}>
                  <FieldsComponent />
                </PartContainer>
              </CollapseContainer>
              <React.Fragment key={resetId}>
                {report.reportType === ReportType.Tabular && (
                  <TabularContainer>
                    <ReportEditor />
                  </TabularContainer>
                )}
              </React.Fragment>
              <DeveloperPanelWrapper />
              {versionHistoryOpen && <VersionHistoryPanel />}
            </Box>
          </Stack>
          <CustomDragLayer />
        </FieldDestinationProvider>
      </GlobalDndProvider>
    </ExportStateContextProvider>
  );
};

export default EditReportPage;
